import { defineMessages } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';

import type { UploadRejectionData } from '@atlaskit/media-picker';

import type { ShowFlagArgs } from '@confluence/flags/entry-points/FlagsStateContainer';

const attachmentSizeErrorFlagI18n = defineMessages({
	errorTitle: {
		id: 'fabric-media-support.attachment-size-limit-error.title',
		defaultMessage: '{fileName} is too large to upload',
		description:
			"Message describing that the chosen file could not be uploaded due to the file's size",
	},
	errorDescription: {
		id: 'fabric-media-support.attachment-size-limit-error.description',
		defaultMessage: 'Attachments must be {size} or less.',
		description: 'Message describing what the maximum size limit for attachments is set to',
	},
	learnMore: {
		id: 'fabric-media-support.attachment-size-limit-error.learn-more',
		defaultMessage: 'Learn more',
		description: 'Link for users who want to understand why they are receiving this error message',
	},
});

const sizeLimitLearnMoreLink = 'https://support.atlassian.com/confluence-cloud/docs/upload-a-file/';

const attachmentZeroByteErrorFlagI18n = defineMessages({
	errorTitle: {
		id: 'fabric-media-support.attachment-zero-byte-error.title',
		defaultMessage: '{fileName} is empty (zero bytes)',
		description: 'Message describing that the chosen file has no contents, i.e. has zero bytes',
	},
	errorDescription: {
		id: 'fabric-media-support.attachment-zero-byte.description',
		defaultMessage: 'Attachments must be at least 1 byte of size',
		description: 'Message describing what the minimum size is',
	},
});

/**
 * Convert size limit bytes using base-2 conversion
 * @param bytes The number of bytes to convert
 * @param maxMagnitude The magnitude of the maximum unit desired ie: 2 -> MB
 */
export const getSizeLimitString = (bytes: number, maxMagnitude?: number): String => {
	if (bytes <= 0) return '0 bytes';

	const k = 1024;
	const decimals = 2;
	const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	let maxIndex = units.length - 1;

	let exponent = Math.floor(Math.log(bytes) / Math.log(k));
	if (maxMagnitude && maxMagnitude < maxIndex) {
		maxIndex = maxMagnitude;
	}
	exponent = exponent > maxIndex ? maxIndex : exponent;

	return `${parseFloat((bytes / Math.pow(k, exponent)).toFixed(decimals))} ${units[exponent]}`;
};

export const getAttachmentSizeLimitErrorFlag = (
	data: UploadRejectionData,
	intl: IntlShape,
): ShowFlagArgs => {
	if (data.reason === 'fileSizeLimitExceeded') {
		return {
			title: intl.formatMessage(attachmentSizeErrorFlagI18n.errorTitle, {
				fileName: data.fileName,
			}),
			description: intl.formatMessage(attachmentSizeErrorFlagI18n.errorDescription, {
				size: getSizeLimitString(data.limit),
			}),
			actions: [
				{
					content: intl.formatMessage(attachmentSizeErrorFlagI18n.learnMore),
					onClick: () => {
						window.open(sizeLimitLearnMoreLink, '_blank');
					},
				},
			],
		};
	} else {
		return {
			title: intl.formatMessage(attachmentZeroByteErrorFlagI18n.errorTitle, {
				fileName: data.fileName,
			}),
			description: intl.formatMessage(attachmentZeroByteErrorFlagI18n.errorDescription),
		};
	}
};
